/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Service User Denied component
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Sergey Mysov
 * @version 1.23.0
 * @since 1.11.0
 */

import type { ReactElement } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { makeSelectAuthoritiesIsServiceUser } from '@/main/pages/PageAuth/selectors/authSelectors';
import type { IRootReducer } from '@/main/reducers/rootReducer';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import type { IArrowTooltipProps } from '@components/common/Tooltip/Tooltip';

import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import type { ICommonTranslation } from '../../internationalization/types';

export type TServiceUserDeniedTooltip = Partial<Pick<IArrowTooltipProps, 'placement'>>;

export interface IServiceUserDenied extends TServiceUserDeniedTooltip {
    tooltipMsg?: string;
    disabled?: boolean;
    renderComponent: (disabled: boolean) => ReactElement;
}

type TServiceUserDenied = IServiceUserDenied & IConnectedProps;

const ServiceUserDenied = (props: TServiceUserDenied) => {
    const { disabled = false, isServiceUser, renderComponent, translationCommon, tooltipMsg, ...tooltipProps } = props;
    return (
        isServiceUser
            ? <ArrowTooltip
                message={tooltipMsg || translationCommon.serviceUser.tooltip}
                placement="top"
                {...tooltipProps}
            >
                <span>{ renderComponent(true) }</span>
            </ArrowTooltip>
            : renderComponent(disabled)
    );
};

interface IConnectedProps {
    isServiceUser: boolean;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isServiceUser: makeSelectAuthoritiesIsServiceUser(),
    translationCommon: makeSelectTranslationCommon(),
});

export default connect(mapStateToProps)(ServiceUserDenied);
